import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, Maximize2, Minimize2, MessageSquare } from 'lucide-react';
import { RiOpenaiFill } from 'react-icons/ri';
import FollowUpQuestions from './FollowUpQuestions';
import { SHOW_QUIZ_FEATURES } from '../config/features';
import '../components/Dashboard.css';

const ChatModal = ({ isOpen, onClose, selectedDocument, user }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [chatInput, setChatInput] = useState('');
    const [chatMessages, setChatMessages] = useState([]);
    const [messageTimestamps, setMessageTimestamps] = useState([]);
    const [chatLoading, setChatLoading] = useState(false);
    const [triggerFollowUp, setTriggerFollowUp] = useState(false);

    // Reset trigger after it's used
    useEffect(() => {
        if (triggerFollowUp) {
            const timer = setTimeout(() => {
                setTriggerFollowUp(false);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [triggerFollowUp]);

    const handleChat = async () => {
        if (!chatInput.trim() || chatLoading) return;

        const userMessage = `You: ${chatInput}`;
        const timestamp = new Date().toLocaleTimeString();
        
        setChatMessages(prev => [...prev, userMessage]);
        setMessageTimestamps(prev => [...prev, timestamp]);
        setChatInput('');
        setChatLoading(true);

        try {
            console.log('Sending chat request with:', {
                message: chatInput,
                user_id: user?.uid,
                document_id: selectedDocument?.id || selectedDocument?.document_id || selectedDocument?.uuid
            });

            // Use the answer_question endpoint (the new backend)
            const response = await fetch('https://celebrated-charisma-production-5701.up.railway.app/answer_question', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    question: chatInput,
                    user_id: user?.uid,
                    document_id: selectedDocument?.id || selectedDocument?.document_id || selectedDocument?.uuid
                })
            });

            console.log('Response status:', response.status);
            console.log('Response ok:', response.ok);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Response error:', errorText);
                throw new Error(`Server error: ${response.status} - ${errorText}`);
            }

            const data = await response.json();
            console.log('Response data:', data);
            const aiMessage = `AI: ${data.answer}`;
            const aiTimestamp = new Date().toLocaleTimeString();
            
            setChatMessages(prev => [...prev, aiMessage]);
            setMessageTimestamps(prev => [...prev, aiTimestamp]);
            
            // Trigger follow-up questions to show after AI responds
            console.log('ChatModal - Triggering follow-up questions via prop');
            setTriggerFollowUp(true);
        } catch (error) {
            console.error('Chat error:', error);
            console.error('Error details:', error.message);
            console.error('Error type:', error.name);
            const errorMessage = `AI: Error: ${error.message}. Please check console for details.`;
            const errorTimestamp = new Date().toLocaleTimeString();
            
            setChatMessages(prev => [...prev, errorMessage]);
            setMessageTimestamps(prev => [...prev, errorTimestamp]);
        } finally {
            setChatLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className={`ask-ai-container ${isExpanded ? 'expanded' : ''}`} onClick={(e) => e.stopPropagation()}>
                <div className="ask-ai-header">
                    <div className="ask-ai-title">
                        <Sparkles className="ask-ai-icon" />
                        <h1>Ask AI</h1>
                    </div>
                    <div className="modal-controls">
                        <button 
                            className="expand-button" 
                            onClick={() => setIsExpanded(!isExpanded)}
                            title={isExpanded ? 'Minimize' : 'Expand'}
                        >
                            {isExpanded ? <Minimize2 size={20} /> : <Maximize2 size={15} />}
                        </button>
                        <button className="close-button" onClick={onClose}>×</button>
                    </div>
                </div>

                <div className="ask-ai-content">
                    {/* Main Explanation Card - Only show when no messages */}
                    {chatMessages.length === 0 && (
                        <>
                            <div className="explanation-card">
                                <p className="explanation-text">
                                    We blend the latest ChatGPT with your own study materials
                                </p>
                                <div className="visual-equation">
                                    <div className="equation-item">
                                        <div className="chatgpt-icon"><RiOpenaiFill className="chatgpt-icon-openai" /> </div>
                                    </div>
                                    <div className="equation-plus">+</div>
                                    <div className="equation-item">
                                        <div className="documents-icon">
                                            <div className="doc-stack">
                                                <div className="doc doc-1"></div>
                                                <div className="doc doc-2"></div>
                                                <div className="doc doc-3"></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="equation-equals">=</div>
                                    <div className="equation-item">
                                        <div className="result-icon">
                                            <Sparkles className="result-sparkle" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Description */}
                            <p className="description-text">
                                {SHOW_QUIZ_FEATURES
                                    ? 'Upload your study materials to generate summaries, quizzes, and answers you can trust.'
                                    : 'Upload your study materials to generate summaries and answers you can trust.'}
                            </p>
                        </>
                    )}


                    {/* Chat Messages (if any) */}
                    {chatMessages.length > 0 && (
                        <div className="chat-messages">
                            {chatMessages.map((message, index) => {
                                const isUser = message.startsWith('You:');
                                const messageContent = message.replace(/^(You:|AI:)\s*/, '');
                                const timestamp = messageTimestamps[index] || '';
                                const isLastAIMessage = !isUser && index === chatMessages.length - 1;
                                
                                return (
                                    <div key={index}>
                                        <div className={`message ${isUser ? 'user-message' : 'ai-message'}`}>
                                            <div className="message-avatar">
                                                {isUser ? 'U' : 'AI'}
                                            </div>
                                            <div className="message-content">
                                                {messageContent}
                                            </div>
                                            {timestamp && (
                                                <div className="message-timestamp">
                                                    {timestamp}
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Show follow-up questions after the last AI message */}
                                        {isLastAIMessage && (
                                            <FollowUpQuestions
                                                selectedDocument={selectedDocument}
                                                user={user}
                                                showAfterDelay={500}
                                                triggerShow={triggerFollowUp}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                            {chatLoading && (
                                <div className="message ai-message">
                                    <div className="message-avatar">AI</div>
                                    <div className="typing-indicator">
                                        <div className="typing-dots">
                                            <div className="typing-dot"></div>
                                            <div className="typing-dot"></div>
                                            <div className="typing-dot"></div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Chat Input */}
                    <div className="ask-ai-input">
                        <input 
                            type="text" 
                            value={chatInput} 
                            onChange={(e) => setChatInput(e.target.value)}
                            placeholder="Ask AI anything"
                            onKeyPress={(e) => e.key === 'Enter' && handleChat()}
                        />
                        <button onClick={handleChat} disabled={chatLoading} className="send-btn">
                            <span className="send-icon">↑</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChatModal;
